// Slice 45 — DSR queue tab. 2-col layout: left table (status / type /
// customer / deadline meter / received) + right detail card with verify /
// fulfil / reject actions.

import { useState } from "react";
import { DSR_KINDS, DsrApi, type DsrKind, type DsrRow, type DsrStatus } from "../../api/client";
import { useShell } from "../../shell/HqShellContext";
import { HqAvatar, HqBadge, HqButton, HqCard, HqInput, HqModal, Icons } from "../../ui";
import { MonoId } from "../licenses/MonoId";
import { type Urgency, daysLeft, deadlineUrgency, fmtDateMs } from "./format";

const KIND_LABEL: Record<DsrKind, string> = {
  access: "Access · Art. 15",
  erase: "Erase · Art. 17",
  rectify: "Rectify · Art. 16",
  portability: "Portability · Art. 20",
  object: "Object · Art. 21",
};

const STATUS_LABEL: Record<DsrStatus, string> = {
  open: "Open",
  verifying: "Verifying ID",
  fulfilled: "Fulfilled",
  rejected: "Rejected",
};

const URGENCY_TONE: Record<Urgency, "danger" | "warn" | "accent" | "neutral"> = {
  danger: "danger",
  warn: "warn",
  ok: "accent",
  closed: "neutral",
};

export interface DsrTabProps {
  rows: DsrRow[];
  onMutated: () => void;
}

export function DsrTab({ rows, onMutated }: DsrTabProps) {
  const [selectedId, setSelectedId] = useState<string | null>(rows[0]?.id ?? null);
  const [createOpen, setCreateOpen] = useState(false);

  const selected = rows.find((r) => r.id === selectedId) ?? rows[0] ?? null;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 16, minHeight: 540 }}>
      <HqCard
        title="DSR queue"
        icon={Icons.Gavel}
        right={
          <HqButton
            size="sm"
            variant="primary"
            leadingIcon={Icons.Plus}
            onClick={() => setCreateOpen(true)}
          >
            New DSR
          </HqButton>
        }
        padding={false}
      >
        <Table rows={rows} selectedId={selected?.id ?? null} onSelect={(id) => setSelectedId(id)} />
      </HqCard>
      <Detail dsr={selected} onMutated={onMutated} onSelect={setSelectedId} />
      <CreateDsrModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(id) => {
          setCreateOpen(false);
          setSelectedId(id);
          onMutated();
        }}
      />
    </div>
  );
}

function Table({
  rows,
  selectedId,
  onSelect,
}: {
  rows: DsrRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  if (rows.length === 0) {
    return (
      <div
        style={{
          padding: 32,
          textAlign: "center",
          color: "var(--hq-text-dim)",
          fontSize: 12.5,
        }}
      >
        No data-subject requests yet.
      </div>
    );
  }
  return (
    <div style={{ maxHeight: 540, overflow: "auto" }} className="hq-scroll">
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr
            style={{
              position: "sticky",
              top: 0,
              background: "var(--hq-bg-raised)",
              borderBottom: "1px solid var(--hq-border)",
              textAlign: "left",
              zIndex: 1,
            }}
          >
            <Th>Subject</Th>
            <Th>Kind</Th>
            <Th>Status</Th>
            <Th>Deadline</Th>
            <Th>Received</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <Row key={r.id} row={r} active={r.id === selectedId} onClick={() => onSelect(r.id)} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <td
      style={{
        padding: "10px 12px",
        color: "var(--hq-text-dim)",
        fontFamily: "var(--hq-font-mono)",
        fontSize: 10.5,
        letterSpacing: ".1em",
        textTransform: "uppercase",
      }}
    >
      {children}
    </td>
  );
}

function Row({
  row,
  active,
  onClick,
}: {
  row: DsrRow;
  active: boolean;
  onClick: () => void;
}) {
  const urgency = deadlineUrgency(row.deadlineAt, row.closedAt);
  return (
    <tr
      onClick={onClick}
      style={{
        cursor: "pointer",
        borderBottom: "1px solid var(--hq-border)",
        borderLeft: active ? "2px solid var(--hq-accent)" : "2px solid transparent",
        background: active ? "var(--hq-bg-hover)" : "transparent",
      }}
      onMouseEnter={(e) => {
        if (!active)
          (e.currentTarget as HTMLTableRowElement).style.background = "rgba(255,255,255,.02)";
      }}
      onMouseLeave={(e) => {
        if (!active) (e.currentTarget as HTMLTableRowElement).style.background = "transparent";
      }}
    >
      <td style={{ padding: "10px 12px" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <HqAvatar name={row.emailMasked} size={24} />
          <span className="hq-mono" style={{ fontSize: 11 }}>
            {row.emailMasked}
          </span>
        </span>
      </td>
      <td style={{ padding: "10px 12px" }}>
        <HqBadge tone="neutral" size="sm">
          {row.kind}
        </HqBadge>
      </td>
      <td style={{ padding: "10px 12px" }}>
        <HqBadge
          tone={
            row.status === "fulfilled"
              ? "success"
              : row.status === "rejected"
                ? "danger"
                : row.status === "verifying"
                  ? "warn"
                  : "accent"
          }
          size="sm"
        >
          {STATUS_LABEL[row.status]}
        </HqBadge>
      </td>
      <td style={{ padding: "10px 12px", minWidth: 140 }}>
        <DeadlineMeter deadlineAt={row.deadlineAt} closedAt={row.closedAt} urgency={urgency} />
      </td>
      <td style={{ padding: "10px 12px" }}>
        <span className="hq-mono" style={{ fontSize: 11, color: "var(--hq-text-dim)" }}>
          {fmtDateMs(row.receivedAt)}
        </span>
      </td>
    </tr>
  );
}

function DeadlineMeter({
  deadlineAt,
  closedAt,
  urgency,
}: {
  deadlineAt: number;
  closedAt: number | null;
  urgency: Urgency;
}) {
  if (closedAt != null) {
    return (
      <span className="hq-mono" style={{ fontSize: 10.5, color: "var(--hq-text-dim)" }}>
        closed {fmtDateMs(closedAt)}
      </span>
    );
  }
  const dl = daysLeft(deadlineAt);
  const tone = URGENCY_TONE[urgency];
  const color =
    tone === "danger"
      ? "var(--hq-danger)"
      : tone === "warn"
        ? "var(--hq-warn)"
        : "var(--hq-accent)";
  // 30-d clock fill (inverted — empty bar at received, full at deadline).
  const totalDays = 30;
  const elapsed = Math.max(0, Math.min(totalDays, totalDays - dl));
  const pct = (elapsed / totalDays) * 100;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span className="hq-mono" style={{ fontSize: 10.5, color }}>
        {dl <= 0 ? "OVERDUE" : `${dl}d left`}
      </span>
      <div
        style={{
          height: 4,
          width: "100%",
          background: "var(--hq-bg-input)",
          borderRadius: 99,
          overflow: "hidden",
        }}
      >
        <div style={{ width: `${pct}%`, height: "100%", background: color }} />
      </div>
    </div>
  );
}

function Detail({
  dsr,
  onMutated,
  onSelect,
}: {
  dsr: DsrRow | null;
  onMutated: () => void;
  onSelect: (id: string | null) => void;
}) {
  const { toast } = useShell();
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [fulfilOpen, setFulfilOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  if (!dsr) {
    return (
      <HqCard title="Detail" icon={Icons.Gavel}>
        <div
          style={{
            padding: 24,
            textAlign: "center",
            color: "var(--hq-text-dim)",
            fontSize: 12.5,
          }}
        >
          Select a DSR to review.
        </div>
      </HqCard>
    );
  }

  const closed = dsr.status === "fulfilled" || dsr.status === "rejected";

  return (
    <HqCard
      title="Detail"
      icon={Icons.Gavel}
      right={
        <HqBadge
          tone={
            dsr.status === "fulfilled"
              ? "success"
              : dsr.status === "rejected"
                ? "danger"
                : dsr.status === "verifying"
                  ? "warn"
                  : "accent"
          }
          size="sm"
        >
          {STATUS_LABEL[dsr.status]}
        </HqBadge>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <HqAvatar name={dsr.emailMasked} size={36} />
          <div style={{ flex: 1 }}>
            <div className="hq-mono" style={{ fontSize: 12 }}>
              {dsr.emailMasked}
            </div>
            <div className="hq-mono" style={{ fontSize: 10, color: "var(--hq-text-dim)" }}>
              {dsr.id}
            </div>
          </div>
        </div>
        <Meta dsr={dsr} />
        <IdentityCard dsr={dsr} />
        {dsr.resolution && <ResolutionCard text={dsr.resolution} closed={closed} />}
        {!closed && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {!dsr.verified ? (
              <HqButton
                variant="primary"
                size="sm"
                leadingIcon={Icons.Check}
                onClick={() => setVerifyOpen(true)}
              >
                Verify identity
              </HqButton>
            ) : (
              <HqButton
                variant="primary"
                size="sm"
                leadingIcon={Icons.Sparkles}
                onClick={() => setFulfilOpen(true)}
              >
                Fulfil + send response
              </HqButton>
            )}
            <HqButton
              variant="ghost"
              size="sm"
              leadingIcon={Icons.X}
              onClick={() => setRejectOpen(true)}
            >
              Reject (with reason)
            </HqButton>
          </div>
        )}
      </div>

      <VerifyModal
        open={verifyOpen}
        dsr={dsr}
        onClose={() => setVerifyOpen(false)}
        onDone={() => {
          setVerifyOpen(false);
          onMutated();
          toast("Identity verified · DSR moved to verifying.", "success");
        }}
      />
      <FulfilModal
        open={fulfilOpen}
        dsr={dsr}
        onClose={() => setFulfilOpen(false)}
        onDone={(exportBundle, erased) => {
          setFulfilOpen(false);
          onMutated();
          if (exportBundle) downloadJson(`${dsr.id}.gdpr-art15.json`, exportBundle);
          toast(
            erased
              ? "DSR fulfilled · customer erased."
              : exportBundle
                ? "DSR fulfilled · export downloaded."
                : "DSR fulfilled.",
            "success",
          );
        }}
      />
      <RejectModal
        open={rejectOpen}
        dsr={dsr}
        onClose={() => setRejectOpen(false)}
        onDone={() => {
          setRejectOpen(false);
          onMutated();
          toast("DSR rejected · reason audited.", "warn");
        }}
      />

      {closed && (
        <div style={{ marginTop: 12 }}>
          <HqButton variant="ghost" size="sm" onClick={() => onSelect(null)}>
            Clear selection
          </HqButton>
        </div>
      )}
    </HqCard>
  );
}

function Meta({ dsr }: { dsr: DsrRow }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
      <MetaCell label="Kind" value={KIND_LABEL[dsr.kind]} />
      <MetaCell
        label="Customer"
        value={dsr.customerId ? <MonoId value={dsr.customerId} short={12} /> : "(unlinked)"}
      />
      <MetaCell label="Received" value={fmtDateMs(dsr.receivedAt)} mono />
      <MetaCell label="Statutory deadline" value={fmtDateMs(dsr.deadlineAt)} mono />
    </div>
  );
}

function MetaCell({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div
      style={{
        padding: 8,
        background: "var(--hq-bg-input)",
        border: "1px solid var(--hq-border-input)",
        borderRadius: "var(--hq-radius-md)",
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <span
        className="hq-mono"
        style={{
          fontSize: 9.5,
          color: "var(--hq-text-dim)",
          letterSpacing: ".1em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 12,
          fontFamily: mono ? "var(--hq-font-mono)" : "var(--hq-font-sans)",
          color: "var(--hq-text)",
        }}
      >
        {value}
      </span>
    </div>
  );
}

function IdentityCard({ dsr }: { dsr: DsrRow }) {
  return (
    <div
      style={{
        padding: 12,
        background: dsr.verified ? "var(--hq-success-soft)" : "var(--hq-warn-soft)",
        border: `1px solid ${dsr.verified ? "rgba(52,211,153,.25)" : "rgba(251,191,36,.25)"}`,
        borderRadius: "var(--hq-radius-md)",
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      {dsr.verified ? (
        <Icons.Check width={14} height={14} sw={1.8} style={{ color: "var(--hq-success)" }} />
      ) : (
        <Icons.Lock width={14} height={14} sw={1.8} style={{ color: "var(--hq-warn)" }} />
      )}
      <div style={{ flex: 1, fontSize: 12 }}>
        <strong style={{ color: dsr.verified ? "var(--hq-success)" : "var(--hq-warn)" }}>
          {dsr.verified ? "Identity verified" : "Identity not verified"}
        </strong>
        <div style={{ color: "var(--hq-text-muted)", marginTop: 2, lineHeight: 1.45 }}>
          {dsr.verificationNote ??
            (dsr.verified
              ? "(no note)"
              : "Confirm via passkey assertion or signed-out-of-band channel.")}
        </div>
      </div>
    </div>
  );
}

function ResolutionCard({ text, closed }: { text: string; closed: boolean }) {
  return (
    <div
      style={{
        padding: 10,
        background: "var(--hq-bg-input)",
        border: "1px solid var(--hq-border-input)",
        borderRadius: "var(--hq-radius-md)",
        fontSize: 12,
        color: "var(--hq-text-muted)",
        lineHeight: 1.5,
        fontStyle: "italic",
        whiteSpace: "pre-wrap",
      }}
    >
      <span
        className="hq-mono"
        style={{
          fontSize: 9.5,
          color: "var(--hq-text-dim)",
          letterSpacing: ".1em",
          textTransform: "uppercase",
          display: "block",
          marginBottom: 6,
        }}
      >
        {closed ? "Resolution" : "Draft"}
      </span>
      {text}
    </div>
  );
}

function CreateDsrModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [email, setEmail] = useState("");
  const [kind, setKind] = useState<DsrKind>("access");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (!email.includes("@")) {
      setErr("Email looks malformed.");
      return;
    }
    setBusy(true);
    setErr(null);
    const res = await DsrApi.create(email.trim(), kind);
    setBusy(false);
    if (!res.ok) {
      setErr(res.error.message);
      return;
    }
    setEmail("");
    setKind("access");
    onCreated(res.data?.id ?? "");
  }

  return (
    <HqModal
      open={open}
      onClose={onClose}
      title="Open a new DSR"
      width={440}
      footer={
        <>
          <HqButton variant="ghost" onClick={onClose}>
            Cancel
          </HqButton>
          <HqButton variant="primary" loading={busy} onClick={() => void submit()}>
            Open DSR
          </HqButton>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Field label="Subject email" hint="Auto-links to a customer if the address matches.">
          <HqInput
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="subject@example.com"
            autoFocus
          />
        </Field>
        <Field label="Kind">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {DSR_KINDS.map((k) => (
              <button
                key={k}
                type="button"
                onClick={() => setKind(k)}
                style={{
                  padding: "6px 10px",
                  borderRadius: "var(--hq-radius-md)",
                  background: kind === k ? "var(--hq-bg-active)" : "var(--hq-bg-input)",
                  border: `1px solid ${kind === k ? "rgba(34,211,238,.45)" : "var(--hq-border-input)"}`,
                  color: "var(--hq-text)",
                  fontSize: 11.5,
                  cursor: "pointer",
                }}
              >
                {KIND_LABEL[k]}
              </button>
            ))}
          </div>
        </Field>
        {err && (
          <div
            style={{
              padding: "8px 12px",
              borderRadius: "var(--hq-radius-md)",
              background: "var(--hq-danger-soft)",
              color: "var(--hq-danger)",
              fontSize: 12,
              border: "1px solid rgba(251,113,133,.25)",
            }}
          >
            {err}
          </div>
        )}
      </div>
    </HqModal>
  );
}

function VerifyModal({
  open,
  dsr,
  onClose,
  onDone,
}: {
  open: boolean;
  dsr: DsrRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const [method, setMethod] = useState("passkey");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    setBusy(true);
    setErr(null);
    const res = await DsrApi.verify(dsr.id, method, note.trim() || undefined);
    setBusy(false);
    if (!res.ok) {
      setErr(res.error.message);
      return;
    }
    onDone();
  }

  return (
    <HqModal
      open={open}
      onClose={onClose}
      title="Verify subject identity"
      width={440}
      footer={
        <>
          <HqButton variant="ghost" onClick={onClose}>
            Cancel
          </HqButton>
          <HqButton
            variant="primary"
            leadingIcon={Icons.Check}
            loading={busy}
            onClick={() => void submit()}
          >
            Verified
          </HqButton>
        </>
      }
    >
      <p style={{ margin: "0 0 12px", fontSize: 12.5, color: "var(--hq-text-muted)" }}>
        Spec §5.5: identity check happens before fulfilment. Both the method and the (optional) note
        land in the audit log.
      </p>
      <Field label="Method">
        <HqInput value={method} onChange={(e) => setMethod(e.target.value)} mono />
      </Field>
      <Field label="Note (optional)">
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          style={{
            width: "100%",
            background: "var(--hq-bg-input)",
            color: "var(--hq-text)",
            border: "1px solid var(--hq-border-input)",
            borderRadius: "var(--hq-radius-lg)",
            padding: "10px 12px",
            fontSize: 12.5,
            fontFamily: "var(--hq-font-sans)",
            resize: "vertical",
            outline: "none",
          }}
        />
      </Field>
      {err && (
        <div
          style={{
            marginTop: 10,
            padding: "8px 12px",
            borderRadius: "var(--hq-radius-md)",
            background: "var(--hq-danger-soft)",
            color: "var(--hq-danger)",
            fontSize: 12,
            border: "1px solid rgba(251,113,133,.25)",
          }}
        >
          {err}
        </div>
      )}
    </HqModal>
  );
}

function FulfilModal({
  open,
  dsr,
  onClose,
  onDone,
}: {
  open: boolean;
  dsr: DsrRow;
  onClose: () => void;
  onDone: (exportBundle: unknown, erased: boolean) => void;
}) {
  const [resolution, setResolution] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (resolution.trim().length < 4) {
      setErr("Resolution required (min 4 chars).");
      return;
    }
    setBusy(true);
    setErr(null);
    const res = await DsrApi.fulfil(dsr.id, resolution.trim());
    setBusy(false);
    if (!res.ok) {
      setErr(res.error.message);
      return;
    }
    onDone(res.data.exportBundle, res.data.erased);
  }

  return (
    <HqModal
      open={open}
      onClose={onClose}
      title={`Fulfil DSR · ${KIND_LABEL[dsr.kind]}`}
      width={520}
      footer={
        <>
          <HqButton variant="ghost" onClick={onClose}>
            Cancel
          </HqButton>
          <HqButton
            variant="primary"
            leadingIcon={Icons.Check}
            loading={busy}
            onClick={() => void submit()}
          >
            Fulfil
          </HqButton>
        </>
      }
    >
      <p
        style={{
          margin: "0 0 12px",
          fontSize: 12.5,
          color: "var(--hq-text-muted)",
          lineHeight: 1.55,
        }}
      >
        {dsr.kind === "access" &&
          "Triggers an Art. 15 export bundle download. Hand it to the subject + record the channel in the resolution."}
        {dsr.kind === "erase" &&
          "Triggers an Art. 17 tombstone on the linked customer. Irreversible. Audit rows survive."}
        {dsr.kind !== "access" &&
          dsr.kind !== "erase" &&
          "Records the resolution + closes the DSR. Manual processing per spec."}
      </p>
      <Field label="Resolution (audited, min 4 chars)">
        <textarea
          value={resolution}
          onChange={(e) => setResolution(e.target.value)}
          rows={4}
          style={{
            width: "100%",
            background: "var(--hq-bg-input)",
            color: "var(--hq-text)",
            border: "1px solid var(--hq-border-input)",
            borderRadius: "var(--hq-radius-lg)",
            padding: "10px 12px",
            fontSize: 12.5,
            fontFamily: "var(--hq-font-sans)",
            resize: "vertical",
            outline: "none",
          }}
        />
      </Field>
      {err && (
        <div
          style={{
            marginTop: 10,
            padding: "8px 12px",
            borderRadius: "var(--hq-radius-md)",
            background: "var(--hq-danger-soft)",
            color: "var(--hq-danger)",
            fontSize: 12,
            border: "1px solid rgba(251,113,133,.25)",
          }}
        >
          {err}
        </div>
      )}
    </HqModal>
  );
}

function RejectModal({
  open,
  dsr,
  onClose,
  onDone,
}: {
  open: boolean;
  dsr: DsrRow;
  onClose: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit() {
    if (reason.trim().length < 4) {
      setErr("Reason required (min 4 chars).");
      return;
    }
    setBusy(true);
    setErr(null);
    const res = await DsrApi.reject(dsr.id, reason.trim());
    setBusy(false);
    if (!res.ok) {
      setErr(res.error.message);
      return;
    }
    onDone();
  }

  return (
    <HqModal
      open={open}
      onClose={onClose}
      title={`Reject DSR · ${KIND_LABEL[dsr.kind]}`}
      width={440}
      footer={
        <>
          <HqButton variant="ghost" onClick={onClose}>
            Cancel
          </HqButton>
          <HqButton
            variant="danger"
            leadingIcon={Icons.X}
            loading={busy}
            onClick={() => void submit()}
          >
            Reject
          </HqButton>
        </>
      }
    >
      <p
        style={{
          margin: "0 0 12px",
          fontSize: 12.5,
          color: "var(--hq-text-muted)",
          lineHeight: 1.55,
        }}
      >
        Rejection reason lands in the audit log + is returned to the subject (Art. 12). Customer
        keeps right of appeal.
      </p>
      <Field label="Reason">
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={4}
          style={{
            width: "100%",
            background: "var(--hq-bg-input)",
            color: "var(--hq-text)",
            border: "1px solid var(--hq-border-input)",
            borderRadius: "var(--hq-radius-lg)",
            padding: "10px 12px",
            fontSize: 12.5,
            fontFamily: "var(--hq-font-sans)",
            resize: "vertical",
            outline: "none",
          }}
        />
      </Field>
      {err && (
        <div
          style={{
            marginTop: 10,
            padding: "8px 12px",
            borderRadius: "var(--hq-radius-md)",
            background: "var(--hq-danger-soft)",
            color: "var(--hq-danger)",
            fontSize: 12,
            border: "1px solid rgba(251,113,133,.25)",
          }}
        >
          {err}
        </div>
      )}
    </HqModal>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span style={{ fontSize: 11.5, color: "var(--hq-text-muted)", fontWeight: 500 }}>
          {label}
        </span>
        {hint && <span style={{ fontSize: 10.5, color: "var(--hq-text-dim)" }}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function downloadJson(filename: string, data: unknown) {
  const blob = new Blob([`${JSON.stringify(data, null, 2)}\n`], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
